import { useQuery } from '@tanstack/react-query';
import type {
  SmartBuilderCatalogue,
  SmartFilterCode,
  SmartFilterOptions,
  SmartReportPeriod,
  SmartReportRequest,
  SmartReportResult,
} from '@optiflow/types';
import { api } from '@/services/apiClient';

export const smartKeys = {
  catalogue: ['reports', 'smart', 'catalogue'] as const,
  options: (
    metric: string,
    variant: string | null,
    filter: SmartFilterCode,
    period: SmartReportPeriod | null,
  ) => ['reports', 'smart', 'options', metric, variant, filter, period] as const,
  run: (request: SmartReportRequest) => ['reports', 'smart', 'run', request] as const,
};

/** What this account may build. Every choice in the builder comes from here; the browser invents none. */
export function useSmartCatalogueQuery({ enabled = true } = {}) {
  return useQuery({
    queryKey: smartKeys.catalogue,
    queryFn: () => api.get<SmartBuilderCatalogue>('/reports/smart/catalogue'),
    enabled,
  });
}

/** The choices for one filter, listed by the server from the same result the tab lists them from. */
export function useSmartOptionsQuery(
  metric: string,
  variant: string | null,
  filter: SmartFilterCode,
  period: SmartReportPeriod | null,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: smartKeys.options(metric, variant, filter, period),
    queryFn: () =>
      api.get<SmartFilterOptions>('/reports/smart/options', {
        params: {
          metric,
          filter,
          ...(variant ? { variant } : {}),
          ...(period ? { preset: period.preset } : {}),
          ...(period?.preset === 'CUSTOM' ? { from: period.from, to: period.to } : {}),
        },
      }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Runs one Smart Report. The request travels in a POST body so the choices never enter a URL; the
 * server rebuilds and revalidates the plan every time, so changing a filter is simply a new request.
 */
export function useSmartRunQuery(request: SmartReportRequest | null) {
  return useQuery({
    queryKey: request ? smartKeys.run(request) : ['reports', 'smart', 'run', 'idle'],
    queryFn: () => api.post<SmartReportResult>('/reports/smart/run', request),
    enabled: request !== null,
  });
}
